<template>
  <div class="spray-recommendation-table">
    <div class="spray-recommendation-title">
      {{ $t('sprayRecommendation.title') }}
    </div>
    <div class="spray-recommendation-grid-wrapper">
      <DxDataGrid
        class="spray-recommendation-grid"
        :data-source="rows"
        :show-borders="false"
        :row-alternation-enabled="true"
        :column-auto-width="true"
        :show-row-lines="true"
        :show-column-lines="false"
      >
        <DxColumn
          data-field="date"
          :caption="$t('sprayRecommendation.date')"
          :allow-sorting="false"
        />
        <DxColumn
          data-field="frostRisk"
          :caption="$t('sprayRecommendation.frost')"
          :allow-sorting="false"
          cell-template="acronymCell"
        />
        <DxColumn
          data-field="sprayCondition"
          :caption="$t('sprayRecommendation.spray')"
          :allow-sorting="false"
          cell-template="acronymCell"
        />
        <DxColumn
          data-field="precipitationRisk"
          :caption="$t('sprayRecommendation.precipitation')"
          :allow-sorting="false"
          cell-template="acronymCell"
        />
        <DxColumn
          data-field="windRisk"
          :caption="$t('sprayRecommendation.wind')"
          :allow-sorting="false"
          cell-template="acronymCell"
        />
        <DxColumn
          data-field="recommendationLabel"
          :caption="$t('sprayRecommendation.application')"
          :allow-sorting="false"
          cell-template="applicationCell"
        />

        <template #acronymCell="{ data }">
          <span
            class="ac"
            tabindex="0"
            role="button"
            :aria-label="data.data[`${data.column.dataField}Full`]"
            @click.stop="toggleTooltip($event, `${data.rowIndex}-${data.column.dataField}`, data.data[`${data.column.dataField}Full`])"
            @keydown.enter.prevent="toggleTooltip($event, `${data.rowIndex}-${data.column.dataField}`, data.data[`${data.column.dataField}Full`])"
            @keydown.space.prevent="toggleTooltip($event, `${data.rowIndex}-${data.column.dataField}`, data.data[`${data.column.dataField}Full`])"
          >{{ data.value }}</span>
        </template>

        <template #applicationCell="{ data }">
          <span
            class="tag"
            :class="tagClass(data.data.recommendationLevel)"
          >{{ data.value }}</span>
        </template>
      </DxDataGrid>
    </div>
    <div
      v-if="legend.length > 0"
      class="spray-recommendation-legend"
    >
      <span
        v-for="entry in legend"
        :key="entry.acronym"
        class="spray-recommendation-legend-item"
      >
        <strong>{{ entry.acronym }}</strong>: {{ entry.text }}
      </span>
    </div>
    <div
      v-if="activeTooltip"
      class="ac-tooltip"
      :style="{ top: `${activeTooltip.top}px`, left: `${activeTooltip.left}px` }"
    >
      {{ activeTooltip.text }}
    </div>
  </div>
</template>

<script>
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid';
import moment from 'moment';

const TAG_CLASS_BY_LEVEL = {
  GREEN: 'tag-green',
  ORANGE: 'tag-orange',
  RED: 'tag-red',
};
const DEFAULT_TAG_CLASS = 'tag-neutral';

// Acrónimos por columna (solo para las columnas de detalle: la columna final
// "Aplicación" ya lleva emoji + texto corto y se deja sin abreviar, es la
// conclusión de la fila). Las claves son los textos exactos que devuelve
// timac.utils.spray_recommendation (backend) para cada clasificador.
const FROST_RISK_ACRONYMS = {
  'Sin riesgo importante': 'SRI',
  'Baja-media': 'BM',
  'Media (posible radiativa)': 'MPR',
  'Alta (helada probable)': 'AHP',
  'Muy alta (helada radiativa)': 'MAHR',
};
const SPRAY_CONDITION_ACRONYMS = {
  'Precaución: humedad elevada (ΔT < 2 °C)': 'HE',
  'Óptimo (ΔT 2–8 °C)': 'OPT',
  'Precaución: evaporación elevada (ΔT 8–10 °C)': 'EE',
  'No recomendable: evaporación muy elevada (ΔT > 10 °C)': 'EME',
};
const PRECIPITATION_RISK_ACRONYMS = {
  'Sin lluvia relevante': 'SLR',
  'Llovizna posible (0.1–1 mm)': 'LLP',
  'Lluvia probable (1–2 mm)': 'LP',
  'Lluvia alta (≥2 mm)': 'LA',
};
const WIND_RISK_ACRONYMS = {
  'Muy bajo (<1 m/s) - Riesgo de inversión': 'MB',
  'Adecuado (1–3 m/s)': 'AD',
  'Moderado (3–5 m/s) - Riesgo de deriva': 'MOD',
  'Alto (>5 m/s) - No recomendable': 'ALT',
};
// Orden fijo: agrupa la leyenda por columna (Helada, Pulverización, Precipitación,
// Viento) en vez de mezclarlas, para que sea más fácil de escanear.
const LEGEND_COLUMNS = [
  { field: 'frost_risk', acronyms: FROST_RISK_ACRONYMS },
  { field: 'spray_condition', acronyms: SPRAY_CONDITION_ACRONYMS },
  { field: 'precipitation_risk', acronyms: PRECIPITATION_RISK_ACRONYMS },
  { field: 'wind_risk', acronyms: WIND_RISK_ACRONYMS },
];

export default {
  name: 'SprayRecommendationTable',
  components: {
    DxDataGrid,
    DxColumn,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // Tooltip disparado por click/tap (no por hover: en tablet no hay hover,
      // así que el "title" nativo nunca se veía). Un único mecanismo sirve para
      // ratón y para táctil por igual.
      activeTooltip: null,
    };
  },
  watch: {
    items() {
      this.activeTooltip = null;
    },
  },
  mounted() {
    document.addEventListener('click', this.closeTooltip);
    document.addEventListener('scroll', this.closeTooltip, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeTooltip);
    document.removeEventListener('scroll', this.closeTooltip, true);
  },
  computed: {
    rows() {
      const noData = this.$t('sprayRecommendation.noData');
      return this.items.map((item) => ({
        date: item.date ? moment(item.date).format('DD/MM') : noData,
        frostRisk: this.abbreviate(item.frost_risk, FROST_RISK_ACRONYMS, noData),
        frostRiskFull: item.frost_risk ?? noData,
        sprayCondition: this.abbreviate(item.spray_condition, SPRAY_CONDITION_ACRONYMS, noData),
        sprayConditionFull: item.spray_condition ?? noData,
        precipitationRisk: this.abbreviate(item.precipitation_risk, PRECIPITATION_RISK_ACRONYMS, noData),
        precipitationRiskFull: item.precipitation_risk ?? noData,
        windRisk: this.abbreviate(item.wind_risk, WIND_RISK_ACRONYMS, noData),
        windRiskFull: item.wind_risk ?? noData,
        recommendationLabel: item.recommendation_label ?? noData,
        recommendationLevel: item.recommendation_level ?? null,
      }));
    },
    legend() {
      const seenAcronyms = new Set();
      const entries = [];
      LEGEND_COLUMNS.forEach(({ field, acronyms }) => {
        this.items.forEach((item) => {
          const text = item[field];
          const acronym = text != null ? acronyms[text] : null;
          if (!acronym || seenAcronyms.has(acronym)) return;
          seenAcronyms.add(acronym);
          entries.push({ acronym, text });
        });
      });
      return entries;
    },
  },
  methods: {
    abbreviate(text, acronyms, noData) {
      if (text == null) return noData;
      return acronyms[text] ?? text;
    },
    tagClass(level) {
      return TAG_CLASS_BY_LEVEL[level] ?? DEFAULT_TAG_CLASS;
    },
    toggleTooltip(event, key, text) {
      if (this.activeTooltip && this.activeTooltip.key === key) {
        this.activeTooltip = null;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.activeTooltip = {
        key,
        text,
        top: rect.bottom + 6,
        left: rect.left + rect.width / 2,
      };
    },
    closeTooltip() {
      this.activeTooltip = null;
    },
  },
};
</script>

<style scoped>
.spray-recommendation-table {
  /* flex, no width:100%: comparte fila con las tarjetas del pronóstico y ocupa
  el hueco que sobre en la última fila; min-width:0 es necesario para que,
  como flex item, pueda encogerse por debajo del ancho intrínseco de la
  tabla en vez de forzar overflow horizontal de toda la página. */
  flex: 1 1 420px;
  min-width: 0;

  /* Tarjeta propia (mismo lenguaje que .content-devx del resto de la app):
  para que se lea como un módulo distinto dentro del mismo widget de clima,
  no como una tabla suelta flotando junto a las tarjetas de previsión. */
  background: #ffffff;
  border: 1px solid #e4e6ed;
  border-radius: 6px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  padding: 10px 12px 12px;
}

.spray-recommendation-title {
  font-size: 12px;
  font-weight: 600;
  color: #52565e;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding-bottom: 8px;
}

.spray-recommendation-grid-wrapper {
  width: 100%;
  overflow-x: auto;
}

.spray-recommendation-grid::v-deep .dx-header-row {
  background: transparent;
}

.spray-recommendation-grid::v-deep .dx-header-row td[role='columnheader'] {
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #8a8f98;
  border-bottom: 1px solid #e4e6ed;
}

.spray-recommendation-grid::v-deep .dx-row > td {
  border-bottom: 1px solid #eef0f3;
  padding-top: 6px;
  padding-bottom: 6px;
}

.spray-recommendation-grid::v-deep .dx-row-alt > td {
  background: #fafbfc;
}

/* Sigla con subrayado de puntos: indica que hay más información al tocar/
pulsar, sin depender solo de la leyenda de debajo. El tooltip se dispara por
click/tap (no por hover) para que funcione igual en tablet que en escritorio. */
.ac {
  border-bottom: 1px dotted #8a8f98;
  cursor: pointer;
  font-weight: 600;
}
.ac:focus-visible {
  outline: 2px solid #003caa;
  outline-offset: 2px;
}

.ac-tooltip {
  position: fixed;
  transform: translateX(-50%);
  background: #1c2430;
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.78em;
  line-height: 1.4;
  max-width: 220px;
  text-align: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  z-index: 1000;
  pointer-events: none;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.92em;
  white-space: nowrap;
}
.tag-green {
  background: #e2f5e9;
  color: #1f7a43;
}
.tag-orange {
  background: #fbe4d5;
  color: #b3541c;
}
.tag-red {
  background: #fbdede;
  color: #b3271c;
}
.tag-neutral {
  background: #eeeeee;
  color: #555555;
}

.spray-recommendation-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eef0f3;
  font-size: 0.75em;
  color: #666;
}
</style>
